import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery } from "convex/react";
import { useNavigate } from "react-router";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { parseCurrency, centsToInput } from "@/lib/format";
import { formatLocalDateAsUTC, formatDateForQuery } from "@/lib/date-utils";
import { TimePicker } from "@/components/ui/time-picker";
import { DatePicker } from "../ui/date-picker";
import { RouteScreenshotInput } from "./route-screenshot-input";
import { parseISO, subDays, startOfDay, endOfDay } from "date-fns";
import { AlertCircle } from "lucide-react";
import { getErrorMessage } from "@/lib/errors";
import { useSettings } from "@/hooks/use-settings";
import {
  validateBlock,
  checkHoursLimit,
  HOUR_LIMITS,
  type ExistingBlock,
} from "@/lib/block-validation";

interface BlockFormProps {
  mode: "create" | "edit";
  blockId?: Id<"blocks">;
  initialData?: {
    date: string;
    shiftStart: string;
    shiftEnd: string;
    basePay: number;
    tip: number;
    noRoutePayout: number;
    station?: string;
    manualMiles?: number;
    notes?: string;
    routeScreenshotId?: Id<"_storage">;
    stops?: number;
    locations?: number;
    packages?: number;
  };
  onSuccess?: () => void;
}

export function BlockForm({ mode, blockId, initialData, onSuccess }: BlockFormProps) {
  const navigate = useNavigate();
  const createBlock = useMutation(api.blocks.createBlock);
  const updateBlock = useMutation(api.blocks.updateBlock);
  const updateSettings = useMutation(api.settings.updateSettings);
  const { settings } = useSettings();

  // In edit mode, fetch the block data
  const blockData = useQuery(
    api.blocks.getBlock,
    mode === "edit" && blockId ? { id: blockId } : "skip"
  );

  const [date, setDate] = useState<Date | undefined>(
    mode === "edit" && blockData
      ? parseISO(blockData.date)
      : new Date()
  )

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showExtendedHoursDialog, setShowExtendedHoursDialog] = useState(false);

  // Calculate week boundaries for validation query
  const weekDates = useMemo(() => {
    if (!date) return null;
    const start = subDays(startOfDay(date), 6);
    const end = endOfDay(date);
    return {
      weekStartDate: formatDateForQuery(start),
      weekEndDate: formatDateForQuery(end),
      dateString: formatLocalDateAsUTC(date),
    };
  }, [date]);

  // Fetch validation data when date changes
  const validationData = useQuery(
    api.blocks.getBlockValidationData,
    weekDates
      ? {
          date: weekDates.dateString,
          weekStartDate: weekDates.weekStartDate,
          weekEndDate: weekDates.weekEndDate,
        }
      : "skip"
  );

  // Form state - use blockData in edit mode, initialData as fallback
  const dataSource = mode === "edit" ? blockData : initialData;

  const [shiftStart, setShiftStart] = useState(dataSource?.shiftStart ?? "");
  const [shiftEnd, setShiftEnd] = useState(dataSource?.shiftEnd ?? "");
  const [basePay, setBasePay] = useState(
    dataSource ? centsToInput(dataSource.basePay) : ""
  );
  const [tip, setTip] = useState(
    dataSource ? centsToInput(dataSource.tip) : ""
  );
  const [noRoutePayout, setNoRoutePayout] = useState(
    dataSource ? centsToInput(dataSource.noRoutePayout) : ""
  );
  const [manualMiles, setManualMiles] = useState(
    dataSource?.manualMiles ? dataSource.manualMiles.toString() : ""
  );
  const [station, setStation] = useState(
    dataSource?.station ?? settings?.defaultStation ?? ""
  );
  const [notes, setNotes] = useState(dataSource?.notes ?? "");
  const [routeScreenshotId, setRouteScreenshotId] = useState<Id<"_storage"> | undefined>(
    dataSource?.routeScreenshotId
  );
  const [actualHours, setActualHours] = useState("");
  const [stops, setStops] = useState(
    dataSource?.stops ? dataSource.stops.toString() : ""
  );
  const [locations, setLocations] = useState(
    dataSource?.locations ? dataSource.locations.toString() : ""
  );
  const [packages, setPackages] = useState(
    dataSource?.packages ? dataSource.packages.toString() : ""
  );

  // Update form when blockData loads in edit mode
  useEffect(() => {
    if (mode === "edit" && blockData) {
      setDate(parseISO(blockData.date));
      setShiftStart(blockData.shiftStart);
      setShiftEnd(blockData.shiftEnd);
      setBasePay(centsToInput(blockData.basePay));
      setTip(centsToInput(blockData.tip));
      setNoRoutePayout(centsToInput(blockData.noRoutePayout));
      setManualMiles(blockData.manualMiles ? blockData.manualMiles.toString() : "");
      setStation(blockData.station ?? "");
      setNotes(blockData.notes ?? "");
      setRouteScreenshotId(blockData.routeScreenshotId);
      setActualHours(
        blockData.actualMinutes
          ? (blockData.actualMinutes / 60).toFixed(1)
          : ""
      );
      setStops(blockData.stops ? blockData.stops.toString() : "");
      setLocations(blockData.locations ? blockData.locations.toString() : "");
      setPackages(blockData.packages ? blockData.packages.toString() : "");
    }
  }, [mode, blockData]);

  // Convert validation data to ExistingBlock format
  const existingBlocks: ExistingBlock[] = useMemo(() => {
    if (!validationData?.blocksOnDate) return [];
    return validationData.blocksOnDate.map((b) => ({
      _id: b._id,
      date: b.date,
      shiftStart: b.shiftStart,
      shiftEnd: b.shiftEnd,
    }));
  }, [validationData]);

  const performSubmit = async (enableExtendedHours = false) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // If enabling extended hours, update settings first
      if (enableExtendedHours) {
        await updateSettings({ extendedHours: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to enable extended hours");
      setIsSubmitting(false);
      return;
    }

    // Parse manual miles
    const parsedManualMiles = manualMiles.trim()
      ? parseFloat(manualMiles)
      : undefined;
    const validManualMiles = parsedManualMiles && !isNaN(parsedManualMiles) && parsedManualMiles > 0
      ? parsedManualMiles
      : undefined;
    const trimmedNotes = notes.trim() || undefined;

    // Parse actual hours to minutes
    const parsedActualHours = actualHours.trim()
      ? parseFloat(actualHours)
      : undefined;
    const actualMinutes = parsedActualHours && !isNaN(parsedActualHours) && parsedActualHours > 0
      ? Math.round(parsedActualHours * 60)
      : undefined;

    // Parse workload values
    const parsedStops = stops.trim() ? parseInt(stops, 10) : undefined;
    const validStops = parsedStops && !isNaN(parsedStops) && parsedStops > 0
      ? parsedStops
      : undefined;

    const parsedLocations = locations.trim() ? parseInt(locations, 10) : undefined;
    const validLocations = parsedLocations && !isNaN(parsedLocations) && parsedLocations > 0
      ? parsedLocations
      : undefined;

    const parsedPackages = packages.trim() ? parseInt(packages, 10) : undefined;
    const validPackages = parsedPackages && !isNaN(parsedPackages) && parsedPackages > 0
      ? parsedPackages
      : undefined;

    try {
      const dateString = formatLocalDateAsUTC(date!);
      if (mode === "create") {
        await createBlock({
          date: dateString,
          shiftStart,
          shiftEnd,
          basePay: parseCurrency(basePay),
          tip: parseCurrency(tip),
          noRoutePayout: parseCurrency(noRoutePayout),
          station: station.trim() || undefined,
          manualMiles: validManualMiles,
          notes: trimmedNotes,
          routeScreenshotId,
          actualMinutes,
          stops: validStops,
          locations: validLocations,
          packages: validPackages,
        });
      } else if (mode === "edit" && blockId) {
        await updateBlock({
          id: blockId,
          date: dateString,
          shiftStart,
          shiftEnd,
          basePay: parseCurrency(basePay),
          tip: parseCurrency(tip),
          noRoutePayout: parseCurrency(noRoutePayout),
          station: station.trim() || undefined,
          manualMiles: validManualMiles,
          notes: trimmedNotes,
          routeScreenshotId,
          clearRouteScreenshot: !routeScreenshotId && !!blockData?.routeScreenshotId,
          actualMinutes,
          stops: validStops,
          locations: validLocations,
          packages: validPackages,
        });
      }

      onSuccess?.();
      navigate("/blocks");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic required field validation
    if (!date || !shiftStart || !shiftEnd || !basePay || !station) {
      setError("Please fill in all required fields");
      return;
    }

    const basePayCents = parseCurrency(basePay);
    if (basePayCents <= 0) {
      setError("Base pay must be greater than 0");
      return;
    }

    // Block-specific validation
    const dateString = formatLocalDateAsUTC(date);
    const blockErrors = validateBlock(
      dateString,
      shiftStart,
      shiftEnd,
      existingBlocks,
      mode === "edit" ? blockId : undefined
    );

    if (blockErrors.length > 0) {
      setError(blockErrors[0].message);
      return;
    }

    // Hours limit validation - only for new blocks
    // When editing, the block's hours are already counted in the total
    if (mode === "create") {
      const currentWeeklyHours = validationData?.weeklyHours ?? 0;
      const hoursCheck = checkHoursLimit(
        shiftStart,
        shiftEnd,
        currentWeeklyHours,
        settings?.extendedHours ?? false
      );

      if (hoursCheck.exceedsLimit) {
        if (hoursCheck.wouldFitWithExtended && !settings?.extendedHours) {
          // Can fit with extended hours - show dialog
          setShowExtendedHoursDialog(true);
          return;
        } else {
          // Would exceed even extended hours
          const limit = settings?.extendedHours
            ? HOUR_LIMITS.extended.weekly
            : HOUR_LIMITS.regular.weekly;
          setError(
            `This block would put you at ${hoursCheck.totalAfterBlock.toFixed(1)}hr, exceeding your ${limit}hr weekly limit.`
          );
          return;
        }
      }
    }

    // All validation passed - submit
    await performSubmit();
  };

  const hoursCheck = shiftStart && shiftEnd
    ? checkHoursLimit(shiftStart, shiftEnd, validationData?.weeklyHours ?? 0, settings?.extendedHours ?? false)
    : null;

  return (
    <>
      {/* Extended Hours Confirmation Dialog */}
      <AlertDialog open={showExtendedHoursDialog} onOpenChange={setShowExtendedHoursDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable Extended Hours?</AlertDialogTitle>
            <AlertDialogDescription>
              This block would put you at {hoursCheck?.totalAfterBlock.toFixed(1)}hr for the week,
              exceeding your 40hr regular limit. Would you like to enable extended hours (50hr/week)?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setShowExtendedHoursDialog(false);
              performSubmit(true);
            }}>
              Enable Extended Hours
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
        <h1 className="text-xl font-semibold">
          {mode === "create" ? "Add Block" : "Edit Block"}
        </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="flex items-start gap-3 rounded-lg bg-amber-50/80 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-700/30 p-3">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-800 dark:text-slate-200">{error}</p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="date" className="text-base font-medium">
            Block Date *
          </Label>
          <DatePicker
            date={date}
            onSelect={setDate}
            required
          />
        </div>

        {/* Station - Required */}
        <div className="space-y-2">
          <Label htmlFor="station" className="text-base font-medium">
            Station *
          </Label>
          {settings?.stations && settings.stations.length > 0 ? (
            <Select value={station} onValueChange={setStation}>
              <SelectTrigger>
                <SelectValue placeholder="Select station" />
              </SelectTrigger>
              <SelectContent>
                {settings.stations.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                    {s === settings.defaultStation && " (default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id="station"
              type="text"
              placeholder="e.g., DAX5, DLA8"
              value={station}
              onChange={(e) => setStation(e.target.value)}
              required
            />
          )}
          {(!settings?.stations || settings.stations.length === 0) && (
            <p className="text-xs text-muted-foreground">
              Add stations in Settings to use quick select
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="shiftStart" className="text-sm text-muted-foreground">
              Start Time *
            </Label>
            <TimePicker
              time={shiftStart}
              onSelect={setShiftStart}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="shiftEnd" className="text-sm text-muted-foreground">
              End Time *
            </Label>
            <TimePicker
              time={shiftEnd}
              onSelect={setShiftEnd}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="basePay" className="text-base font-medium">
            Base Pay ($) *
          </Label>
          <Input
            id="basePay"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={basePay}
            onChange={(e) => setBasePay(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label className="text-base font-medium">
            Additional Earnings
          </Label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="tip" className="text-sm text-muted-foreground">
                Tip ($)
              </Label>
              <Input
                id="tip"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={tip}
                onChange={(e) => setTip(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="noRoutePayout" className="text-sm text-muted-foreground">
                Adjustments ($)
              </Label>
              <Input
                id="noRoutePayout"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={noRoutePayout}
                onChange={(e) => setNoRoutePayout(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Mileage Section */}
        <div className="space-y-2">
          <Label htmlFor="manualMiles" className="text-base font-medium">
            Mileage (Optional)
          </Label>
          <Input
            id="manualMiles"
            type="number"
            inputMode="decimal"
            step="0.1"
            min="0"
            placeholder="0.0"
            value={manualMiles}
            onChange={(e) => setManualMiles(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Enter miles if you didn't use GPS tracking
          </p>
        </div>

        {/* Actual Hours Section */}
        <div className="space-y-2">
          <Label htmlFor="actualHours" className="text-base font-medium">
            Actual Hours (Optional)
          </Label>
          <Input
            id="actualHours"
            type="number"
            inputMode="decimal"
            step="0.5"
            min="0"
            placeholder="0.0"
            value={actualHours}
            onChange={(e) => setActualHours(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            How long did this block actually take? Used for true hourly rate.
          </p>
        </div>

        {/* Workload Section */}
        <div className="space-y-2">
          <Label className="text-base font-medium">
            Workload (Optional)
          </Label>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="stops" className="text-sm text-muted-foreground">
                Stops
              </Label>
              <Input
                id="stops"
                type="number"
                inputMode="numeric"
                min="0"
                placeholder="0"
                value={stops}
                onChange={(e) => setStops(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="locations" className="text-sm text-muted-foreground">
                Locations
              </Label>
              <Input
                id="locations"
                type="number"
                inputMode="numeric"
                min="0"
                placeholder="0"
                value={locations}
                onChange={(e) => setLocations(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="packages" className="text-sm text-muted-foreground">
                Packages
              </Label>
              <Input
                id="packages"
                type="number"
                inputMode="numeric"
                min="0"
                placeholder="0"
                value={packages}
                onChange={(e) => setPackages(e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            From Amazon Flex itinerary summary
          </p>
        </div>

        {/* Route Screenshot Section */}
        <div className="space-y-2">
          <Label className="text-base font-medium">
            Route Screenshot (Optional)
          </Label>
          <RouteScreenshotInput
            value={routeScreenshotId}
            onChange={setRouteScreenshotId}
          />
          <p className="text-xs text-muted-foreground">
            Capture your route from Amazon Flex app
          </p>
        </div>

        {/* Notes Section */}
        <div className="space-y-2">
          <Label htmlFor="notes" className="text-base font-medium">
            Notes (Optional)
          </Label>
          <Textarea
            id="notes"
            placeholder="Heavy packages, bad route, etc."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="min-h-[80px] resize-none"
          />
        </div>

        <div className="flex flex-col gap-3">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="h-12 text-base font-medium"
          >
            {isSubmitting
              ? "Saving..."
              : mode === "create"
                ? "Add Block"
                : "Save Changes"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
            className="h-12 text-base font-medium"
          >
            Cancel
          </Button>
        </div>
      </form>
      </div>
    </>
  );
}